/**
 * Friendly placeholder rendered for routes that are flagged as
 * `comingSoon: true` in the route registry.
 *
 * The component is intentionally *very* small and self-contained — it
 * has no Supabase, react-query or i18n dependencies — so a stubbed
 * page can never crash the app shell. Adding new "Bald verfügbar"
 * pages is a one-line change in the registry; this file only needs
 * editing if the visual treatment changes.
 *
 * Why a dedicated component instead of an inline JSX block?
 *   • Reusable from both `<AppRoutes>` (full-page placeholder) and
 *     ad-hoc dashboard widgets that want to advertise an upcoming
 *     feature.
 *   • Keeps the `AppRoutes.tsx` renderer focused on routing logic.
 */

import { Hammer } from 'lucide-react';

interface Props {
  /** Optional title; defaults to the German "Bald verfügbar". */
  title?: string;
  /** Optional explanatory note shown below the title. */
  note?: string;
}

export function ComingSoon({ title = 'Bald verfügbar', note }: Props) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center min-h-[60vh] p-8 text-center"
    >
      <div className="max-w-md space-y-4">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Hammer className="w-6 h-6 text-primary" aria-hidden="true" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">
          {note ??
            'Diese Funktion ist bereits geplant und befindet sich aktuell in der Entwicklung. ' +
              'Sobald sie freigeschaltet ist, erscheint sie automatisch hier — ohne dass Sie die App neu laden müssen.'}
        </p>
      </div>
    </div>
  );
}

/**
 * Compact "Bald" badge used by the sidebar to mark coming-soon entries
 * inline with the rest of the navigation. Kept here so the visual
 * treatment lives next to the full-page placeholder.
 */
export function ComingSoonBadge() {
  return (
    <span
      className="ml-auto text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 rounded-full px-2 py-0.5"
      title="Diese Funktion befindet sich noch in der Entwicklung"
    >
      Bald
    </span>
  );
}
